import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import WorkflowDefinitionContainer from "modules/analyst/containers/WorkflowDefinition";
import { Header } from "build-ts/metadata";
import { ScheduledJobProto } from "build-ts/schedule";
import { buildWorkflowTag } from "modules/chat/utils/chatUtils";
import AnalysisRunsViewerContainer from "modules/analyst/containers/AnalysisRunsViewer";
import CustomModal from "components/modals/CustomModal";
import WorkflowRunsOutputListContainer from "modules/workflow/containers/WorkflowRunsOutputList";

interface JobDetailViewProps {
  onBack: () => void;
  selectedJob?: Header;
  onJobNameChange?: (newName: string) => void;
  /** Pre-fetched full proto — forwarded to WorkflowDetailContainer to skip a redundant API call. */
  initialJobDetails?: ScheduledJobProto;
  /** When provided, tabs are URL-driven — the container owns the active index. */
  activeTab?: number;
  onTabChange?: (idx: number) => void;
  /** Darwin internal workflow name — owned by JobDetailViewContainer. */
  darwinName: string;
  setDarwinName: (name: string) => void;
  /** Called after any successful workflow save — owned by JobDetailViewContainer. */
  onWorkflowSaved?: (savedJob?: ScheduledJobProto) => void;
  /** Increments after a save completes — triggers a re-fetch in WorkflowDefinitionContainer. */
  refreshCounter: number;
}

const JobDetailView: React.FC<JobDetailViewProps> = ({
  onBack,
  selectedJob,
  onJobNameChange,
  initialJobDetails,
  activeTab: externalActiveTab,
  onTabChange: externalOnTabChange,
  darwinName,
  setDarwinName,
  onWorkflowSaved,
  refreshCounter,
}) => {
  const navigate = useNavigate();
  const [internalActiveTab, setInternalActiveTab] = useState<number>(0);

  // Inline edit mode for description + instructions + guidance fields
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  // Mirrors isSavingAll from WorkflowDefinitionContainer for the header Save button
  const [isSavingAll, setIsSavingAll] = useState<boolean>(false);
  // Filled by WorkflowDefinitionContainer so the header Save button can trigger handleSaveAll
  const saveHandlerRef = useRef<(() => Promise<void>) | null>(null);

  // Use external tab control if provided, otherwise fall back to internal state
  const activeTab = externalActiveTab !== undefined ? externalActiveTab : internalActiveTab;
  const setActiveTab = (idx: number) => {
    if (externalOnTabChange) externalOnTabChange(idx);
    else setInternalActiveTab(idx);
  };
  const [triggerNameSave, setTriggerNameSave] = useState<boolean>(false);
  const { isOpen: isExecutionLogOpen, onOpen: onExecutionLogOpen, onClose: onExecutionLogClose } = useDisclosure();

  // Editable job name — persisted via WorkflowFormContainer when triggerNameSave fires
  const [jobName, setJobName] = useState<string>(selectedJob?.name?.trim() || "");
  const [isEditingJobName, setIsEditingJobName] = useState(false);
  const [jobNameDraft, setJobNameDraft] = useState("");

  // Reset state when the selected job changes
  useEffect(() => {
    setInternalActiveTab(0);
    setJobName(selectedJob?.name?.trim() || "");
    setIsEditingJobName(false);
    setJobNameDraft("");
    setIsEditMode(false);
  }, [selectedJob?.id]);

  const jobDisplayName = jobName || selectedJob?.name?.trim() || "New Workflow";

  const handleRunWorkflow = useCallback(() => {
    navigate("/chat/new", {
      state: {
        initialPrompt:
          `I want to run the workflow: ${buildWorkflowTag(darwinName, jobDisplayName)}. Fetch the details of the` +
          " workflow and run it. Ask me If there is any input required to run the workflow.",
        analystId: selectedJob?.owner_id,
      },
    });
  }, [navigate, darwinName, jobDisplayName, selectedJob?.owner_id]);

  const confirmJobNameEdit = useCallback(() => {
    const confirmed = jobNameDraft.trim() || jobDisplayName;
    setJobName(confirmed);
    setIsEditingJobName(false);
    // Notify the parent component that the job name has changed - this is used to update the name in breadcrumb
    onJobNameChange?.(confirmed);
    // Persist the new name for existing jobs
    if (selectedJob?.id) {
      setTriggerNameSave(true);
    }
  }, [jobNameDraft, jobDisplayName, selectedJob?.id, onJobNameChange]);

  return (
    <Box flex="1" minH={0} minW={0} bg="white" display="flex" flexDirection="column" overflow="hidden">
      {/* Title row: back, editable name, Edit + Run */}
      <HStack
        px={4}
        py={2}
        borderBottom="1px solid"
        borderColor="gray.200"
        align="center"
        w="100%"
        spacing={3}
        flexShrink={0}
      >
        <HStack minW={0} flex="1" spacing={2} align="center">
          <IconButton
            aria-label="Back to workflows"
            icon={<Icon as={AppIcons.leftArrow} boxSize={4} />}
            variant="ghost"
            size="sm"
            color="gray.700"
            _hover={{ bg: "gray.100" }}
            onClick={onBack}
            flexShrink={0}
          />

          {isEditingJobName ? (
            <HStack flex="1" minW={0} spacing={2} align="center">
              <Input
                flex="1"
                minW={0}
                size="md"
                variant="flushed"
                fontWeight="600"
                color="gray.800"
                autoFocus
                value={jobNameDraft}
                onChange={e => setJobNameDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") confirmJobNameEdit();
                  else if (e.key === "Escape") setIsEditingJobName(false);
                }}
                borderColor="purple.400"
                _focusVisible={{ borderColor: "purple.500", boxShadow: "none" }}
              />
              <IconButton
                aria-label="Confirm job name"
                icon={<Icon as={AppIcons.check} boxSize={4} color="green.500" />}
                size="xs"
                variant="ghost"
                colorScheme="green"
                flexShrink={0}
                onMouseDown={e => e.preventDefault()}
                onClick={confirmJobNameEdit}
              />
            </HStack>
          ) : (
            <HStack flex="1" minW={0} spacing={1} align="center">
              <Text fontSize="lg" fontWeight="700" color="gray.800" noOfLines={1} mb={0} minW={0} textAlign="left">
                {jobDisplayName}
              </Text>
              <IconButton
                aria-label="Edit workflow name"
                icon={<Icon as={AppIcons.edit} boxSize={3.5} />}
                size="xs"
                variant="ghost"
                colorScheme="gray"
                color="gray.400"
                _hover={{ color: "purple.600", bg: "purple.50" }}
                flexShrink={0}
                onClick={() => {
                  setJobNameDraft(jobDisplayName);
                  setIsEditingJobName(true);
                }}
              />
            </HStack>
          )}
        </HStack>

        {selectedJob?.id && (
          <HStack spacing={2} flexShrink={0}>
            {isEditMode ? (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  fontWeight="600"
                  fontSize="sm"
                  color="gray.500"
                  onClick={() => setIsEditMode(false)}
                  isDisabled={isSavingAll}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="purple"
                  bg="purple.700"
                  color="white"
                  _hover={{ bg: "purple.600" }}
                  _active={{ bg: "purple.800" }}
                  fontWeight="600"
                  fontSize="sm"
                  isLoading={isSavingAll}
                  loadingText="Saving…"
                  onClick={() => saveHandlerRef.current?.()}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  fontWeight="600"
                  fontSize="sm"
                  onClick={() => setIsEditMode(true)}
                  leftIcon={<Icon as={AppIcons.edit} boxSize={4} />}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  colorScheme="purple"
                  bg="purple.700"
                  color="white"
                  _hover={{ bg: "purple.600" }}
                  _active={{ bg: "purple.800" }}
                  fontWeight="600"
                  fontSize="sm"
                  onClick={handleRunWorkflow}
                  leftIcon={<Icon as={AppIcons.runWorkflow} boxSize={4} />}
                >
                  Run
                </Button>
              </>
            )}
          </HStack>
        )}
      </HStack>

      {/* Tabs + content */}
      <Tabs
        variant="line"
        colorScheme="purple"
        index={activeTab}
        onChange={setActiveTab}
        display="flex"
        flexDirection="column"
        flex="1"
        minH={0}
        overflow="hidden"
      >
        <TabList px={4} borderBottom="1px solid" borderColor="gray.200" flexShrink={0}>
          <Tab
            fontWeight="600"
            py={2.5}
            px={1}
            mr={4}
            _selected={{ fontWeight: "700", borderBottomWidth: "2px", borderColor: "purple.700", color: "purple.700" }}
          >
            Definition
          </Tab>
          <Tab
            fontWeight="600"
            py={2.5}
            px={1}
            _selected={{ fontWeight: "700", borderBottomWidth: "2px", borderColor: "purple.700", color: "purple.700" }}
          >
            Past Runs
          </Tab>
        </TabList>

        <TabPanels flex="1" minH={0} overflow="hidden" display="flex" flexDirection="column">
          <TabPanel p={0} flex="1" minH={0} display="flex" flexDirection="column" overflow="hidden">
            <WorkflowDefinitionContainer
              jobName={jobName}
              setJobName={setJobName}
              selectedJob={selectedJob}
              initialJobDetails={initialJobDetails}
              triggerNameSave={triggerNameSave}
              setTriggerNameSave={setTriggerNameSave}
              setDarwinName={setDarwinName}
              onViewExecutionLog={onExecutionLogOpen}
              onWorkflowSaved={onWorkflowSaved}
              isEditMode={isEditMode}
              setIsEditMode={setIsEditMode}
              refreshCounter={refreshCounter}
              saveHandlerRef={saveHandlerRef}
              onSavingChange={setIsSavingAll}
            />
          </TabPanel>
          <TabPanel p={0} flex="1" minH={0} display="flex" flexDirection="column" overflow="hidden">
            <Box display="flex" flexDirection="column" flex="1" minH={0} h="100%" overflow="hidden" px={2}>
              <Box
                maxW="900px"
                mx="auto"
                w="100%"
                flex="1"
                minH={0}
                overflow="hidden"
                display="flex"
                flexDirection="column"
                px={4}
                py={5}
              >
                <WorkflowRunsOutputListContainer selectedJob={selectedJob} isTabActive={activeTab === 1} />
              </Box>
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <CustomModal
        isOpen={isExecutionLogOpen}
        onClose={onExecutionLogClose}
        title="Execution log"
        size="lg"
        maxWidth="640px"
        maxHeight="70vh"
        bodyMaxHeight="calc(70vh - 80px)"
        centerInMainContent
      >
        <AnalysisRunsViewerContainer selectedJob={selectedJob} hideRunDetails={true} />
      </CustomModal>
    </Box>
  );
};

export default JobDetailView;
